/**
 * Spec 054, step 3: the client's passport on its page — what we know of the client, to a template of
 * eleven sections, in versions. People write it here; agents write it as they work, each fact with
 * its source. When someone saved first, the editor shows their text beside yours to merge.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { VersionConflictError } from "@/api/client";
import { ClientPassports, type ClientPassportOut } from "@/api/orders";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea } from "@/components/ui/Field";
import { Sheet } from "@/components/ui/Sheet";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { EmptyNote } from "@/kit";
import { passportLine, revisionLine } from "@/lib/clientChat";
import { Markdown } from "@/lib/markdown";

const passportKey = (clientId: string) => ["client-passport", clientId] as const;

export function ClientPassportBlock({ clientId }: { clientId: string }) {
  const [editing, setEditing] = useState(false);
  const [history, setHistory] = useState(false);
  const passport = useQuery({ queryKey: passportKey(clientId), queryFn: () => ClientPassports.get(clientId) });
  if (passport.isLoading) return <SkeletonList count={1} />;
  if (!passport.data) {
    return <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void passport.refetch()}>Повторить</Button>}>Паспорт не загрузился.</EmptyNote>;
  }
  const p = passport.data;
  return (
    <Card className="flex flex-col gap-2">
      <PassportView passport={p} />
      <div className="flex flex-wrap gap-2">
        <Button size="compact" variant="secondary" onClick={() => setEditing(true)}>{p.body_md ? "Изменить" : "Заполнить"}</Button>
        {p.version > 1 && <Button size="compact" variant="ghost" onClick={() => setHistory(true)}>История</Button>}
      </div>
      <PassportSheet key={editing ? "open" : "shut"} passport={p} open={editing} onClose={() => setEditing(false)} />
      <HistorySheet clientId={clientId} open={history} onClose={() => setHistory(false)} />
    </Card>
  );
}

/** The passport to read; while it is empty — what it is for. */
export function PassportView({ passport: p }: { passport: ClientPassportOut }) {
  if (!p.body_md) {
    return (
      <p className="text-meta text-p-text-muted">
        Паспорт пуст. Его заполняют люди и агенты по ходу работы: компания, контакты и роли, откуда пришёл, стек, системы, инфраструктура, риски.
      </p>
    );
  }
  return (
    <>
      <p className="text-meta text-p-text-muted">{passportLine(p)}</p>
      <Markdown text={p.body_md} className="text-body text-p-text" />
    </>
  );
}

function PassportSheet({ passport: p, open, onClose }: { passport: ClientPassportOut; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  // The text and the version as they were when the editor opened: a refresh meanwhile changes
  // neither, so a save over someone else's is a conflict, never a silent overwrite.
  const [body, setBody] = useState(p.body_md || p.template_md || "");
  const [base] = useState(p.version);
  // Someone saved first: their text, to merge by hand; the next save goes against their version.
  const [newer, setNewer] = useState<ClientPassportOut | null>(null);
  const save = useMutation({
    mutationFn: () => ClientPassports.save(p.client_id, { body_md: body.trim(), version: newer?.version ?? base }),
    onSuccess: () => {
      toast.success("Паспорт сохранён");
      void qc.invalidateQueries({ queryKey: passportKey(p.client_id) });
      onClose();
    },
    onError: (error: Error) => {
      if (error instanceof VersionConflictError) {
        setNewer(error.current as unknown as ClientPassportOut);
        toast.error("Паспорт сохранили, пока ты писал. Перенеси нужное из их текста и сохрани снова.");
      } else toast.error(error.message);
    },
  });
  return (
    <Sheet open={open} onClose={onClose} title={`Паспорт: ${p.client}`}
      footer={<Button block disabled={!body.trim()} loading={save.isPending} loadingText="Сохраняем…" onClick={() => save.mutate()}>Сохранить</Button>}>
      <div className="flex flex-col gap-3">
        {newer && (
          <details open className="space-y-2 rounded-xl border border-p-border px-3 py-2">
            <summary className="min-h-11 cursor-pointer py-2 text-body font-semibold">Сохранили раньше тебя — версия {newer.version}</summary>
            <Markdown text={newer.body_md} className="text-body text-p-text" />
          </details>
        )}
        <TextArea label="Текст паспорта" value={body} onChange={(e) => setBody(e.target.value)} maxLength={30000} className="min-h-[360px]"
          hint="По разделам шаблона. Контакты людей клиента писать можно; пароли и ключи — нет, они хранятся отдельно." />
      </div>
    </Sheet>
  );
}

function HistorySheet({ clientId, open, onClose }: { clientId: string; open: boolean; onClose: () => void }) {
  const revisions = useQuery({ queryKey: [...passportKey(clientId), "revisions"], queryFn: () => ClientPassports.revisions(clientId), enabled: open });
  return (
    <Sheet open={open} onClose={onClose} title="История паспорта">
      {revisions.isLoading && <SkeletonList count={2} />}
      {revisions.isError && <EmptyNote>Не удалось загрузить.</EmptyNote>}
      <ol className="flex flex-col gap-2">
        {(revisions.data ?? []).map((r) => (
          <li key={r.id} className="rounded-xl border border-p-border px-3 py-2">
            <p className="text-meta text-p-text-muted">{revisionLine(r)}</p>
            <details className="space-y-2">
              <summary className="min-h-11 cursor-pointer py-2 text-body font-semibold">Текст</summary>
              <Markdown text={r.body_md} className="text-body text-p-text" />
            </details>
          </li>
        ))}
      </ol>
    </Sheet>
  );
}
